import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeft, ChevronDown, ChevronUp, Clock, Mail, X } from "lucide-react";
import React from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { STAFF_ORDER, staffDetails } from "./staff-data";
import StaffInfoPanel from "./staff-info-panel";

interface StaffDetailProps {
    staffId: string;
    onBack?: () => void;
}

const StaffDetailView: React.FC<StaffDetailProps> = ({ staffId, onBack }) => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = React.useState<"activity" | "metrics">("activity");
    const [rightPanelTab, setRightPanelTab] = React.useState<"basicInfo" | "employeeIdentifiers">("basicInfo");
    
    // Find current staff index in the order
    const currentIndex = STAFF_ORDER.indexOf(staffId);
    const isFirst = currentIndex === 0;
    const isLast = currentIndex === STAFF_ORDER.length - 1;
    
    // Navigation handlers
    const handleNavigateUp = () => {
        if (!isFirst) {
            const previousStaffId = STAFF_ORDER[currentIndex - 1];
            navigate({ to: '/manager/employee-performance/$employeeId', params: { employeeId: previousStaffId } });
        }
    };
    
    const handleNavigateDown = () => {
        if (!isLast) {
            const nextStaffId = STAFF_ORDER[currentIndex + 1];
            navigate({ to: '/manager/employee-performance/$employeeId', params: { employeeId: nextStaffId } });
        }
    };

    const staff = staffDetails[staffId];

    if (!staff) {
        return (
            <div className="flex flex-col w-full p-4">
                <Button 
                    variant="ghost" 
                    onClick={() => onBack ? onBack() : navigate({ to: '/manager/employee-performance' })} 
                    className="w-fit mb-4"
                >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                </Button>
                <p>Staff member not found</p>
            </div>
        );
    }

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "Active":
                return <Badge className="bg-green-600 hover:bg-green-600 text-white">Active</Badge>;
            case "Off":
                return <Badge className="bg-red-500 hover:bg-red-500 text-white">Off</Badge>;
            case "Break":
                return <Badge className="bg-orange-500 hover:bg-orange-500 text-white">Break</Badge>;
            default:
                return <Badge variant="secondary">{status}</Badge>;
        }
    };

    const getPerformanceBadge = (performance: string) => {
        switch (performance) {
            case "Excellent":
                return <Badge className="bg-green-100 text-green-700 hover:bg-green-100">Excellent</Badge>;
            case "Good":
                return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Good</Badge>;
            default:
                return <Badge variant="secondary">{performance}</Badge>;
        }
    };

    return (
        <div className="flex flex-col w-full p-2">
            {/* Header */}
            <div className="flex flex-row items-center justify-between w-full pb-4 border-b">
                <div className="flex flex-row items-center gap-x-2">
                    <Button 
                        variant="ghost" 
                        onClick={() => onBack ? onBack() : navigate({ to: '/manager/employee-performance' })} 
                        className="p-2"
                    >
                        <X className="h-5 w-5" />
                    </Button>
                    <Button 
                        variant="ghost" 
                        className="h-6 w-6 p-0" 
                        onClick={handleNavigateUp}
                        disabled={isFirst}
                    >
                        <ChevronUp className={`h-4 w-4 ${isFirst ? 'text-gray-300' : 'text-gray-500'}`} />
                    </Button>
                    <Button 
                        variant="ghost" 
                        className="h-6 w-6 p-0"
                        onClick={handleNavigateDown}
                        disabled={isLast}
                    >
                        <ChevronDown className={`h-4 w-4 ${isLast ? 'text-gray-300' : 'text-gray-500'}`} />
                    </Button>
                    <div className="flex flex-row items-center gap-x-3">
                        <span className="text-base font-medium">{staff.name}</span>
                    </div>
                </div>
                <div className="flex flex-row items-center gap-x-2">
                    <Button variant="outline" className="gap-x-2 font-normal">
                        <Mail className="h-4 w-4" />
                        Email HR
                    </Button>
                    <Button variant="outline" className="gap-x-2 font-normal">
                        <Clock className="h-4 w-4" />
                        Update Shift Time
                    </Button>
                </div>
            </div>

            {/* Employee Info Row */}
            <div className="flex flex-row items-center gap-x-8 text-sm border-b py-4">
                <div className="flex items-center gap-x-2">
                    <span className="text-gray-500">Employee ID</span>
                    <span className="font-medium">{staff.employeeId}</span>
                </div>
                <div className="flex items-center gap-x-2">
                    <span className="text-gray-500">Contact no:</span>
                    <span className="font-medium underline">{staff.contact}</span>
                </div>
            </div>

            <div className="flex flex-row items-center justify-start p-4 bg-gray-100 border-b">
                {getStatusBadge(staff.status)}
            </div>

            {/* Tabs */}
            <div className="flex flex-row items-center gap-x-2 py-2 border-b">
                <Button 
                    variant={activeTab === "activity" ? "outline" : "ghost"}
                    onClick={() => setActiveTab("activity")}
                    className="font-normal"
                >
                    Activity
                </Button>
                <Button 
                    variant={activeTab === "metrics" ? "outline" : "ghost"}
                    onClick={() => setActiveTab("metrics")}
                    className="font-normal"
                >
                    Metrics
                </Button>
            </div>

            {/* Content Area - Activity View */}
            {activeTab === "activity" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Recent Activity Card */}
                    <div className="lg:col-span-1 mt-4">
                        <h3 className="text-base font-medium mb-4">Recent Activity</h3>
                        <div className="border border-blue-200 rounded-lg p-6 bg-blue-50">
                            <div className="flex flex-row items-start justify-between mb-4">
                                <div className="flex flex-col">
                                    <span className="text-lg font-medium">{staff.recentActivity.name}</span>
                                    <span className="text-sm text-gray-600">{staff.recentActivity.role}</span>
                                </div>
                                {getPerformanceBadge(staff.performance)}
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-600">Processed</span>
                                    <span className="text-2xl font-medium">{staff.recentActivity.processed}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-600">Accuracy</span>
                                    <span className="text-2xl font-medium">{staff.recentActivity.accuracy}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-600">Productivity</span>
                                    <span className="text-2xl font-medium">{staff.recentActivity.productivity}</span>
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-sm text-gray-600">Rating</span>
                                    <span className="text-2xl font-medium">{staff.recentActivity.rating}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Column - Staff Info Panel */}
                    <StaffInfoPanel 
                        staff={staff}
                        rightPanelTab={rightPanelTab}
                        setRightPanelTab={setRightPanelTab}
                    />
                </div>
            )}

            {/* Content Area - Metrics View */}
            {activeTab === "metrics" && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column - Chart with Tabs */}
                    <div className="lg:col-span-1 flex flex-col gap-y-4 mt-4">
                        {/* Tab Navigation for Metrics */}
                        <div className="flex flex-row items-center gap-x-2 bg-gray-100 p-1 rounded-lg w-fit">
                            <Button 
                                variant="ghost" 
                                className="bg-white shadow-sm hover:bg-white font-normal text-sm"
                            >
                                Monthly Trends
                            </Button>
                            <Button 
                                variant="ghost"
                                className="hover:bg-white font-normal text-sm text-gray-600"
                            >
                                Performance Metrics
                            </Button>
                            <Button 
                                variant="ghost"
                                className="hover:bg-white font-normal text-sm text-gray-600"
                            >
                                Goals & Progress
                            </Button>
                        </div>

                        {/* Chart */}
                        <div className="w-full h-[400px] border rounded-lg p-6 bg-white">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart data={staff.metrics.weeklyData}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                                    <XAxis 
                                        dataKey="week" 
                                        axisLine={false}
                                        tickLine={false}
                                    />
                                    <YAxis 
                                        axisLine={false}
                                        tickLine={false}
                                        domain={[0, 100]}
                                    />
                                    <Tooltip />
                                    <Legend 
                                        verticalAlign="bottom" 
                                        height={36}
                                        iconType="square"
                                    />
                                    <Bar 
                                        dataKey="approvals" 
                                        fill="#2563eb" 
                                        name="Approvals"
                                        radius={[4, 4, 0, 0]}
                                    />
                                    <Bar 
                                        dataKey="prescriptions" 
                                        fill="#000000" 
                                        name="Prescriptions Processed"
                                        radius={[4, 4, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Right Column - Staff Info Panel */}
                    <StaffInfoPanel 
                        staff={staff}
                        rightPanelTab={rightPanelTab}
                        setRightPanelTab={setRightPanelTab}
                    />
                </div>
            )}
        </div>
    );
}

export default StaffDetailView;
